import { Permissions } from "@/components/CheckPermission/enum";
import { DOCUMENT_TYPE } from "@/pages-components/quotes/enums";
import type { ChatwootConversation } from "@/services/frontdesk/api/chatwoot/types/conversations.types";
import type {
  RelatedDocument,
  RelatedDocumentGroup,
  RelatedDocumentKind,
  RelatedDocumentsResult,
} from "../../types/related-documents.types";

export const MAX_RELATED_TICKET_IDS = 20;
export const RELATED_DOCUMENTS_PREVIEW_GROUPS = 3;

export const RELATED_DOCUMENT_VIEW_PERMISSIONS: Record<
  RelatedDocumentKind,
  Permissions
> = {
  Quote: Permissions.SHOW_QUOTES,
  Order: Permissions.SHOW_ORDERS,
  DeliveryNote: Permissions.SHOW_DELIVERY_NOTES,
  DeliveryNoteRefund: Permissions.SHOW_DELIVERY_NOTES_REFUND,
  Invoice: Permissions.SHOW_INVOICES,
  InvoiceRefund: Permissions.SHOW_INVOICES_REFUND,
  Receipt: Permissions.SHOW_RECEIPTS,
};

const TYPE_KEYS: Record<RelatedDocumentKind, string> = {
  Quote: "quote",
  Order: "order",
  DeliveryNote: "deliveryNote",
  DeliveryNoteRefund: "deliveryNoteRefund",
  Invoice: "invoice",
  InvoiceRefund: "invoiceRefund",
  Receipt: "receipt",
};

export function relatedDocumentTypeKey(kind: RelatedDocumentKind): string {
  return `frontdesk.relatedDocuments.types.${TYPE_KEYS[kind] ?? "quote"}`;
}

export type RelatedStatus =
  | "open"
  | "created"
  | "closed"
  | "partialClosed"
  | "canceled"
  | "rejected"
  | "refunded"
  | "partiallyRefunded"
  | "waiting"
  | "approved"
  | "paused"
  | "ready"
  | "processing";

const STATUS_BY_NAME: Record<string, RelatedStatus> = {
  open: "open",
  create: "created",
  created: "created",
  closed: "closed",
  closedasinvoice: "closed",
  closedasproformainvoice: "closed",
  closedasreceipt: "closed",
  closedbymultidocuments: "closed",
  manualclose: "closed",
  reconciled: "closed",
  partialclosed: "partialClosed",
  canceled: "canceled",
  canceledirrelvant: "canceled",
  canceledprice: "canceled",
  canceleddeliverytime: "canceled",
  canceledother: "canceled",
  canceledduplicate: "canceled",
  rejected: "rejected",
  rejectedbymanager: "rejected",
  refunded: "refunded",
  partialrefunded: "partiallyRefunded",
  waiting: "waiting",
  waitingforapproval: "waiting",
  waitforprinthouseconfirm: "waiting",
  waitingforpayment: "waiting",
  approved: "approved",
  approvedbymanager: "approved",
  confirmed: "approved",
  paused: "paused",
  ready: "ready",
  processing: "processing",
};

export function relatedDocumentStatus(
  statusName?: string | null
): RelatedStatus | null {
  if (!statusName) return null;
  return STATUS_BY_NAME[statusName.trim().toLowerCase()] ?? null;
}

export type RelatedStatusTone = "info" | "success" | "warning" | "error" | "neutral";

export function relatedStatusTone(status: RelatedStatus | null): RelatedStatusTone {
  switch (status) {
    case "closed":
    case "approved":
    case "ready":
      return "success";
    case "partialClosed":
    case "waiting":
    case "paused":
      return "warning";
    case "canceled":
    case "rejected":
      return "error";
    case "open":
    case "created":
    case "processing":
      return "info";
    default:
      return "neutral";
  }
}

/** Canonical conversation first, then merged members; ERP stores the hub ticket id. */
/** Document types a conversation can be linked to through their ERP TicketId. */
const ERP_TICKET_DOCUMENT_TYPES = new Set<number>([
  DOCUMENT_TYPE.quote,
  DOCUMENT_TYPE.order,
  DOCUMENT_TYPE.deliveryNote,
  DOCUMENT_TYPE.deliveryNoteRefund,
  DOCUMENT_TYPE.invoice,
  DOCUMENT_TYPE.invoiceRefund,
  DOCUMENT_TYPE.receipt,
]);

export function isErpTicketDocumentType(documentType: number | null | undefined): boolean {
  return documentType !== null && documentType !== undefined && ERP_TICKET_DOCUMENT_TYPES.has(documentType);
}

/** Ticket id a new link is written to: the merged conversation's canonical id, else the conversation id. */
export function frontDeskTicketId(
  conversation: Pick<ChatwootConversation, "id" | "merge" | "meta"> | null | undefined
): string | null {
  if (!conversation) return null;
  const merge = conversation.merge ?? conversation.meta?.merge;
  const id = merge?.canonical_conversation_id ?? conversation.id;
  const trimmed = id === undefined || id === null ? "" : String(id).trim();
  return trimmed || null;
}

export function collectRelatedTicketIds(
  conversation: Pick<ChatwootConversation, "id" | "merge" | "meta"> | null | undefined
): string[] {
  if (!conversation) return [];
  const merge = conversation.merge ?? conversation.meta?.merge;
  const ids = [
    merge?.canonical_conversation_id,
    conversation.id,
    ...(merge?.member_conversation_ids ?? []),
  ]
    .filter((id) => id !== undefined && id !== null)
    .map((id) => String(id).trim())
    .filter(Boolean);
  return Array.from(new Set(ids)).slice(0, MAX_RELATED_TICKET_IDS);
}

/** Drops documents the user can't view and re-indents children under the nearest visible ancestor. */
export function filterRelatedDocuments(
  result: RelatedDocumentsResult | null | undefined,
  canView: (kind: RelatedDocumentKind) => boolean
): RelatedDocumentsResult {
  if (!result?.groups?.length) {
    return {
      groups: [],
      totalDocuments: 0,
      activeQuoteCount: result?.activeQuoteCount ?? 0,
      multipleActiveQuotes: !!result?.multipleActiveQuotes,
    };
  }
  const groups: RelatedDocumentGroup[] = [];
  for (const group of result.groups) {
    // ERP sends the root quote only as `rootDocument`; `documents` holds its descendants.
    const listed = group.documents ?? [];
    const root = group.rootDocument;
    const all =
      root && !listed.some((doc) => doc.documentId === root.documentId)
        ? [root, ...listed]
        : listed;
    const byId = new Map(all.map((doc) => [doc.documentId, doc]));
    const visibleParent = (doc: RelatedDocument): RelatedDocument | null => {
      const seen = new Set<string>([doc.documentId]);
      let parentId = doc.parentDocumentId ?? null;
      while (parentId && !seen.has(parentId)) {
        seen.add(parentId);
        const parent = byId.get(parentId);
        if (!parent) return null;
        if (canView(parent.documentType)) return parent;
        parentId = parent.parentDocumentId ?? null;
      }
      return null;
    };
    const depthCache = new Map<string, number>();
    const depthOf = (doc: RelatedDocument): number => {
      const cached = depthCache.get(doc.documentId);
      if (cached !== undefined) return cached;
      depthCache.set(doc.documentId, 0);
      const parent = visibleParent(doc);
      const depth = parent ? depthOf(parent) + 1 : 0;
      depthCache.set(doc.documentId, depth);
      return depth;
    };
    const documents = all
      .filter((doc) => canView(doc.documentType))
      .map((doc) => {
        const parent = visibleParent(doc);
        return {
          ...doc,
          parentDocumentId: parent?.documentId ?? null,
          parentDocumentType: parent?.documentType ?? null,
          depth: depthOf(doc),
        };
      });
    if (documents.length === 0) continue;
    const rootDocument =
      group.rootDocument && canView(group.rootDocument.documentType)
        ? group.rootDocument
        : null;
    groups.push({ ...group, rootDocument, documents });
  }
  return {
    groups,
    totalDocuments: groups.reduce((sum, group) => sum + group.documents.length, 0),
    activeQuoteCount: result.activeQuoteCount ?? 0,
    multipleActiveQuotes: !!result.multipleActiveQuotes,
  };
}

export function splitRelatedGroupsForPreview(
  groups: RelatedDocumentGroup[],
  limit: number = RELATED_DOCUMENTS_PREVIEW_GROUPS
): { preview: RelatedDocumentGroup[]; hiddenGroups: number } {
  const preview = groups.slice(0, Math.max(0, limit));
  return { preview, hiddenGroups: Math.max(0, groups.length - preview.length) };
}

export function relatedDocumentIds(result: RelatedDocumentsResult | null | undefined): Set<string> {
  const ids = new Set<string>();
  result?.groups?.forEach((group) =>
    group.documents?.forEach((doc) => ids.add(String(doc.documentId).toLowerCase()))
  );
  return ids;
}
